import { BaseElement } from '../../../base-element';
import type { EmergencyContactFields, ValidationError } from '../../../../state/form.state';
import '../../../ui/dt-input';
import '../../../ui/dt-card';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export interface EmergencySectionChangeDetail {
  section: 'emergencyContact';
  field: keyof EmergencyContactFields;
  path: string;
  value: string;
}

export class EmergencySection extends BaseElement {
  private _emergency: EmergencyContactFields | null = null;
  private _errors: ValidationError[] = [];

  get emergency(): EmergencyContactFields | null {
    return this._emergency;
  }

  set emergency(value: EmergencyContactFields | null) {
    this._emergency = value;
    this.updateRender();
  }

  get errors(): ValidationError[] {
    return this._errors;
  }

  set errors(value: ValidationError[]) {
    this._errors = value ?? [];
    this.updateRender();
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private getError(path: string): string {
    return this._errors.find((error) => error.field === path)?.message ?? '';
  }

  private emitChange(field: keyof EmergencyContactFields, value: string): void {
    const path = `emergencyContact.${field}`;
    this.emit<EmergencySectionChangeDetail>('section-change', {
      section: 'emergencyContact',
      field,
      path,
      value,
    });
  }

  private emitBlur(field: keyof EmergencyContactFields): void {
    const path = `emergencyContact.${field}`;
    this.emit('section-blur', { section: 'emergencyContact', field, path });
  }

  private attachEventListeners(): void {
    this.$$('dt-input[data-field]').forEach((input) => {
      const field = input.getAttribute('data-field') as keyof EmergencyContactFields | null;
      if (!field) return;
      input.addEventListener('input', (event: Event) => {
        const detail = (event as CustomEvent<{ value: string }>).detail;
        this.emitChange(field, detail?.value ?? '');
      });
      input.addEventListener('blur', () => this.emitBlur(field));
    });
  }

  private updateRender(): void {
    const emergency = this._emergency;

    this.render(`
      <section class="flex flex-col gap-3" data-testid="emergency-section">
        <div class="text-sm font-semibold uppercase tracking-widest text-neutral-600">Emergency Contact</div>
        <dt-card variant="outlined">
          <div class="grid gap-4 md:grid-cols-2">
            <div data-control-path="emergencyContact.name">
              <dt-input
                label="Name"
                value="${escapeHtml(emergency?.name ?? '')}"
                required
                error="${escapeHtml(this.getError('emergencyContact.name'))}"
                data-field="name"
                data-path="emergencyContact.name"
              ></dt-input>
            </div>
            <div data-control-path="emergencyContact.phoneOrNumber">
              <dt-input
                label="Phone/Number"
                value="${escapeHtml(emergency?.phoneOrNumber ?? '')}"
                required
                error="${escapeHtml(this.getError('emergencyContact.phoneOrNumber'))}"
                data-field="phoneOrNumber"
                data-path="emergencyContact.phoneOrNumber"
              ></dt-input>
            </div>
          </div>
        </dt-card>
      </section>
    `);

    this.attachEventListeners();
  }
}

customElements.define('dt-emergency-section', EmergencySection);
